<template>
    <div class="space-y-6">
        <h2 class="text-heading-md">Phone Input Examples</h2>

        <div class="grid grid-cols-2 gap-16">
            <div class="space-y-4">
                <div class="space-y-4">
                    <DsPhoneInput
                        v-model="phoneNumber"
                        :defaultCountry="countryCode"
                        label="Phone number"
                        :preferredCountries="preferredCountries"
                        @countryChanged="countryCode = $event"
                    />

                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Country:</strong> {{ countryCode.name }}</p>
                        <p><strong>Dial code:</strong> +{{ countryCode.dialCode }}</p>
                        <p><strong>ISO2:</strong> {{ countryCode.iso2 }}</p>
                        <p><strong>Phone number:</strong> {{ phoneNumber }}</p>
                    </div>
                </div>

                <div class="space-y-4">
                    <DsPhoneInput
                        v-model="phoneNumberError"
                        :defaultCountry="countryCodeError"
                        error="Enter a valid phone number"
                        label="Phone number with error"
                        :preferredCountries="preferredCountries"
                        @countryChanged="countryCode = $event"
                    />

                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Country:</strong> {{ countryCodeError.name }}</p>
                        <p><strong>Dial code:</strong> +{{ countryCodeError.dialCode }}</p>
                        <p><strong>ISO2:</strong> {{ countryCodeError.iso2 }}</p>
                        <p><strong>Phone number:</strong> {{ phoneNumberError }}</p>
                    </div>
                </div>

                <div class="space-y-4">
                    <DsPhoneInput
                        v-model="phoneNumberHelperText"
                        :defaultCountry="countryCodeHelperText"
                        helperText="This is a helper text"
                        label="Phone number with helper text"
                        :preferredCountries="preferredCountries"
                        @countryChanged="countryCodeHelperText = $event"
                    />

                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Country:</strong> {{ countryCodeHelperText.name }}</p>
                        <p><strong>Dial code:</strong> +{{ countryCodeHelperText.dialCode }}</p>
                        <p><strong>ISO2:</strong> {{ countryCodeHelperText.iso2 }}</p>
                        <p><strong>Phone number:</strong> {{ phoneNumberHelperText }}</p>
                    </div>
                </div>

                <div class="space-y-4">
                    <DsPhoneInput
                        v-model="phoneNumberDisabled"
                        :defaultCountry="countryCodeDisabled"
                        disabled
                        label="Phone number (disabled)"
                        :preferredCountries="preferredCountries"
                        @countryChanged="countryCodeDisabled = $event"
                    />
                </div>
            </div>

            <div class="flex flex-col space-y-8">
                <DsButton
                    size="md"
                    variant="primary"
                    @click="isSheetOpen = true"
                >
                    Open Sheet with Phone Input
                </DsButton>

                <div class="space-y-4">
                    <h3 class="text-heading-sm">Prefilled Phone Numbers</h3>

                    <div class="space-y-2">
                        <DsPhoneInput
                            v-model="usPhoneNumber"
                            :defaultCountry="usCountry"
                            label="US Phone Number"
                            :preferredCountries="preferredCountries"
                            @countryChanged="usCountry = $event"
                        />
                        <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                            <p><strong>Input (E164):</strong> {{ usPhoneNumber }}</p>
                            <p><strong>Country:</strong> {{ usCountry.name }}</p>
                            <p><strong>ISO2:</strong> {{ usCountry.iso2 }}</p>
                        </div>
                    </div>

                    <div class="space-y-2">
                        <DsPhoneInput
                            v-model="ukPhoneNumber"
                            :defaultCountry="ukCountry"
                            label="UK Phone Number"
                            :preferredCountries="preferredCountries"
                            @countryChanged="ukCountry = $event"
                        />
                        <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                            <p><strong>Input (E164):</strong> {{ ukPhoneNumber }}</p>
                            <p><strong>Country:</strong> {{ ukCountry.name }}</p>
                            <p><strong>ISO2:</strong> {{ ukCountry.iso2 }}</p>
                        </div>
                    </div>

                    <div class="space-y-2">
                        <DsPhoneInput
                            v-model="canadaPhoneNumber"
                            :defaultCountry="canadaCountry"
                            label="Canada Phone Number"
                            :preferredCountries="preferredCountries"
                            @countryChanged="canadaCountry = $event"
                        />
                        <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                            <p><strong>Input (E164):</strong> {{ canadaPhoneNumber }}</p>
                            <p><strong>Country:</strong> {{ canadaCountry.name }}</p>
                            <p><strong>ISO2:</strong> {{ canadaCountry.iso2 }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <DsSheet
                v-model:open="isSheetOpen"
                primaryAction="Submit"
                secondaryAction="Cancel"
                title="Phone Input Examples"
                @clickPrimary="validatePhoneNumber"
                @clickSecondary="isSheetOpen = false"
            >
                <div class="space-y-4">
                    <DsPhoneInput
                        v-model="phoneNumberInSheet"
                        :error="hasPhoneNumberError"
                        :helperText
                        label="Phone number"
                        required
                        @countryChanged="handleCountryChangedInSheet"
                    />
                    <div class="space-y-1 rounded-lg bg-ds-primary-50 p-3 text-sm">
                        <p><strong>Phone number:</strong> {{ phoneNumberInSheet }}</p>
                        <p><strong>Country:</strong> {{ countryCodeInSheet?.name }}</p>
                        <p><strong>Dial code:</strong> +{{ countryCodeInSheet?.dialCode }}</p>
                        <p><strong>ISO2:</strong> {{ countryCodeInSheet?.iso2 }}</p>
                    </div>
                </div>
            </DsSheet>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import DsSheet from '@/components/design-system/DsSheet.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsPhoneInput from '@/components/design-system/form/input/DsPhoneInput/DsPhoneInput.vue';
import type { Country } from '@/components/design-system/form/input/DsPhoneInput/phone-input.types';
import { getCountryFromIso2, isValid } from '@/shared/utils/phone-number';
import type { CountryCode } from 'libphonenumber-js';
import { ref } from 'vue';

const preferredCountries: CountryCode[] = ['US', 'CA', 'GB'];

const defaultCountry = getCountryFromIso2('US');

const phoneNumber = ref('');
const countryCode = ref<Country>(defaultCountry);

const phoneNumberError = ref('');
const countryCodeError = ref<Country>(defaultCountry);

const phoneNumberHelperText = ref('');
const countryCodeHelperText = ref<Country>(defaultCountry);

const phoneNumberDisabled = ref('');
const countryCodeDisabled = ref<Country>(defaultCountry);

// Three prefilled examples from different countries
const usPhoneNumber = ref('+14155552671');
const usCountry = ref<Country>(getCountryFromIso2('US'));

const ukPhoneNumber = ref('+442079460958');
const ukCountry = ref<Country>(getCountryFromIso2('GB'));

const canadaPhoneNumber = ref('+12045550123');
const canadaCountry = ref<Country>(getCountryFromIso2('CA'));

const hasPhoneNumberError = ref('');
const phoneNumberInSheet = ref('+14155552671');
const countryCodeInSheet = ref<Country>();

const helperText = ref('');
const isSheetOpen = ref(false);

const validatePhoneNumber = () => {
    if (!isValid(phoneNumberInSheet.value)) {
        hasPhoneNumberError.value = 'Invalid phone number';
        helperText.value = '';
    } else {
        helperText.value = 'Phone number is valid';
        hasPhoneNumberError.value = '';
    }
};

const handleCountryChangedInSheet = (country: Country) => {
    countryCodeInSheet.value = country;
    validatePhoneNumber();
};
</script>
